import React, { useRef } from "react"
import dayjs from "dayjs"
import { Box, IconButton, Typography } from "@mui/material"
import AccessTimeIcon from "@mui/icons-material/AccessTime"
import LocalPrintshopIcon from "@mui/icons-material/LocalPrintshop"
import CommentIcon from "@mui/icons-material/Comment"
import ChevronRightIcon from "@mui/icons-material/ChevronRight"

import { COLORS } from "../../utils"

// Beyond this finger travel, the gesture is a scroll, not a tap (KFC-427)
const TAP_MOVE_TOLERANCE_PX = 10

const ExecutionCard = ({
  item,
  onClick,
  title,
  chips = [],
  children,
  cardSx = {},
  contentSx = {},
  titleSx = {},
  renderPrint
}) => {
	const startTime = item.startTime ? dayjs(item.startTime).format("HH:mm") : item.theoreticalStartTime ? dayjs(item.theoreticalStartTime).format("HH:mm") : ""
	const endTime = item.endTime ? dayjs(item.endTime).format("HH:mm") :item.theoreticalEndTime ? dayjs(item.theoreticalEndTime).format("HH:mm") : ""

	const touchOriginRef = useRef(null)

	const handleClick = () => { if (onClick) onClick(item) }

	const handleTouchStart = (e) => {
		// Touches from a portal rendered by the card (print modal, select menu) bubble here through
		// the React tree, though they are outside the card on screen: they are not taps on the card
		if (!e.currentTarget.contains(e.target)) return
		const touch = e.touches[0]
		if (!touch) return
		touchOriginRef.current = { x: touch.clientX, y: touch.clientY, hasMoved: false }
	}

	const handleTouchMove = (e) => {
		const origin = touchOriginRef.current
		const touch = e.touches[0]
		if (!origin || !touch) return
		if (Math.abs(touch.clientX - origin.x) > TAP_MOVE_TOLERANCE_PX
			|| Math.abs(touch.clientY - origin.y) > TAP_MOVE_TOLERANCE_PX) {
			origin.hasMoved = true
		}
	}

	const handleTouchCancel = () => {
		touchOriginRef.current = null
	}

	const handleTouchEnd = (e) => {
		const origin = touchOriginRef.current
		touchOriginRef.current = null
		if (!origin || origin.hasMoved) return
		if (e.target.closest("button, a, input, [role=\"button\"]")) return

		e.preventDefault()
		if (onClick) onClick(item)
	}

	return (
		<Box
			sx={{ ...styles.tileWrapper, ...cardSx }}
			onClick={handleClick}
			onTouchStart={handleTouchStart}
			onTouchMove={handleTouchMove}
			onTouchEnd={handleTouchEnd}
			onTouchCancel={handleTouchCancel}
		>
			{/* Top */}
			<Box sx={styles.timeStatusRow}>
				{/* hours */}
				<Box sx={styles.timeInfo}>
					{startTime && <><AccessTimeIcon sx={styles.timeIcon} />
					<span style={styles.timeText}>{startTime} - {endTime}</span></>}
				</Box>
				{/* chips */}
				<Box sx={styles.chipContainer}>
					{chips.length > 0 && chips.map((chip, index) =>  (
						<Box key={index} sx={{ ...styles.chip, border: chip.chipBorder, color: chip.chipFont, backgroundColor: chip.chipBackground }}>
							{chip.icon}
							{chip.label && <span style={styles.statusText}>{chip.label}</span>}
						</Box>
					))}
				</Box>
			</Box>

			{/* Content */}
			<Box sx={{ ...styles.panel, ...contentSx }}>
				{/* Title */}
				<div>
					<Typography style={{...styles.title, ...titleSx }}>
						{title}
					</Typography>
				</div>

				{/* content */}
				{children}
			</Box>
			{/* Bottom */}
			<Box sx={styles.actionRow}>
				{!item.isPackaging ? <Box sx={styles.actions}>
					{renderPrint
						? renderPrint(item.objectId)
						: (
							<IconButton
								sx={styles.actionButton}
								onClick={(e) => e.stopPropagation()}
								onTouchEnd={(e) => e.stopPropagation()}
							>
								<LocalPrintshopIcon sx={styles.actionIcon} />
							</IconButton>
						)}
					<IconButton
						sx={styles.actionButton}
						onClick={(e) => e.stopPropagation()}
						onTouchEnd={(e) => e.stopPropagation()}
					>
						<CommentIcon sx={styles.actionIcon} />
					</IconButton>
				</Box> : <Box sx={styles.actions}/>}
				<ChevronRightIcon sx={{...styles.chevronIcon, color: item.status === "LOCKED" ? COLORS.BLACK_400 : COLORS.BLACK_600}} />
			</Box>
		</Box>
	)
}

const styles = {
	tileWrapper: {
		position:"relative",
		borderRadius: "8px",
		padding: "16px",
		cursor: "pointer",
		transition: "box-shadow 0.2s ease",
	},
	timeStatusRow: {
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center",
		marginBottom: "16px",
	},
	timeInfo: {
		display: "flex",
		color: `${COLORS.BLACK800}`,
		alignItems: "center",
		gap: "8px",
		fontSize: "16px",
		lineHeight: "24px",
		fontWeight: 400
	},
	timeIcon: {
		fontSize: "24px",
		color: COLORS.BLACK800,
	},
	timeText: {
		fontSize: "16px",
    lineHeight: "24px",
		fontWeight: 400,
		color: COLORS.BLACK_600,
	},
  chipContainer: {
    display: "flex",
    gap: "16px",
  },
  chip: {
    borderRadius: "50px",
    padding: "4px 14px",
	gap: "6px",
	display: "flex",
	alignItems: "center",
  },
  panel: {
    display: "flex",
    flexDirection: "column",
    padding: "12px",
    borderRadius: "8px",
    gap: "12px",
    marginBottom: "16px",
  },
	actions: {
    position: "relative",
		display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
		gap: "8px",
    alignItems: "center",
	},
	actionButton: {
		padding: "4px",
	},
	actionIcon: {
		fontSize: "24px",
		color: COLORS.BLACK_600,
	},
	title: {
		fontSize: "16px",
		fontWeight: 500,
		color: COLORS.BLACK800,
		display: "flex",
		alignItems: "center",
		gap: "12px",
	},
	actionRow: {
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center",
		gap: "8px",
	},
	chevronIcon: {
		fontSize: "24px",
	},
}

export default ExecutionCard
